import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AttendanceLog } from "@/hooks/admin/attendance";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { AttendanceStatusBadge } from "./AttendanceStatusBadge";

interface AttendanceListProps {
  logs: AttendanceLog[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onUserClick: (user: AttendanceLog["user"]) => void;
}

export const AttendanceList = ({
  logs,
  isLoading,
  page,
  totalPages,
  onPageChange,
  onUserClick,
}: AttendanceListProps) => {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-4 bg-card rounded-lg">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No se encontraron registros de asistencia
      </div>
    );
  }

  const getInitials = (firstName: string | null, lastName: string | null) => {
    const first = firstName?.charAt(0) || "";
    const last = lastName?.charAt(0) || "";
    return (first + last).toUpperCase() || "?";
  };

  const getFullName = (user: AttendanceLog["user"]) => {
    if (!user) return "Usuario desconocido";
    const parts = [user.first_name, user.last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "Sin nombre";
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {logs.map((log) => (
          <div
            key={log.id}
            onClick={() => onUserClick(log.user)}
            className="flex items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-card hover:bg-accent/50 rounded-lg cursor-pointer transition-colors"
          >
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarImage src={log.user?.avatar_url || undefined} />
              <AvatarFallback>
                {getInitials(log.user?.first_name || null, log.user?.last_name || null)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{getFullName(log.user)}</p>
              <div className="flex items-center gap-2 flex-wrap">
                {log.user?.dni && (
                  <p className="text-sm text-muted-foreground">DNI: {log.user.dni}</p>
                )}
                {log.payment_status && (
                  <AttendanceStatusBadge
                    status={log.payment_status}
                    daysRemaining={log.days_remaining ?? undefined}
                  />
                )}
              </div>
              {/* Mobile: show date/time inline below */}
              <p className="text-xs text-muted-foreground mt-1 sm:hidden">
                {format(new Date(log.check_in_at), "dd MMM yyyy", { locale: es })} • {format(new Date(log.check_in_at), "HH:mm", { locale: es })}
              </p>
            </div>

            {/* Desktop: show date/time on the right */}
            <div className="hidden sm:block text-right shrink-0">
              <p className="font-medium">
                {format(new Date(log.check_in_at), "dd MMM yyyy", { locale: es })}
              </p>
              <p className="text-sm text-muted-foreground">
                {format(new Date(log.check_in_at), "HH:mm", { locale: es })}
              </p>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => page > 1 && onPageChange(page - 1)}
                className={page <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => onPageChange(pageNum)}
                    isActive={page === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}

            <PaginationItem>
              <PaginationNext
                onClick={() => page < totalPages && onPageChange(page + 1)}
                className={page >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};
